// Section 13 and 21: the cases already judged (crime, offender, victims, response, whether the
// offender reoffended, and where they are now) and last season's unexplained losses. Props only.
import type { CaseVM, JusticeVM, LossVM } from '../../../viewModels/justice';
import { oneDecimal } from '../../format';
import { buildingLabel, seasonLabel, justiceText as t } from '../../text/en';

function lossText(l: LossVM): string {
  switch (l.crime) {
    case 'theft-food':
    case 'theft-tools':
      return t.loss[l.crime](oneDecimal(l.amount ?? 0));
    case 'assault':
    case 'killing':
      return t.loss[l.crime](l.victim?.name ?? t.none);
    case 'sabotage':
      return t.loss.sabotage(
        l.building === null ? t.none : buildingLabel[l.building].toLowerCase(),
      );
  }
}

export function Losses({ vm }: { vm: JusticeVM }) {
  return (
    <section class="justice__section" aria-labelledby="justice-losses-title">
      <h4 id="justice-losses-title">{t.lossesTitle}</h4>
      {vm.unexplained.length === 0 ? (
        <p class="justice-muted">{t.noLosses}</p>
      ) : (
        <ul class="justice-list">
          {vm.unexplained.map((l, i) => (
            <li key={i}>{lossText(l)}</li>
          ))}
        </ul>
      )}
      {(vm.theft.food > 0 || vm.theft.tools > 0) && (
        <p class="justice-muted">
          {t.stolen(oneDecimal(vm.theft.food), oneDecimal(vm.theft.tools))}
        </p>
      )}
    </section>
  );
}

function CaseRow({ c }: { c: CaseVM }) {
  return (
    <tr>
      <td>{t.when(seasonLabel[c.stamp.season], c.stamp.year)}</td>
      <td>{t.crimes[c.crime]}</td>
      <th scope="row" class="justice-name">
        {c.offender.name}
      </th>
      <td>{c.victims.length === 0 ? t.none : c.victims.map((v) => v.name).join(', ')}</td>
      <td>{c.response === null ? t.notJudged : t.responses[c.response]}</td>
      <td>{c.reoffended ? t.yes : t.no}</td>
      <td>{t.fates[c.fate]}</td>
    </tr>
  );
}

export function ResolvedCases({ vm }: { vm: JusticeVM }) {
  return (
    <section class="justice__section" aria-labelledby="justice-resolved-title">
      <h4 id="justice-resolved-title">{t.resolvedTitle}</h4>
      {vm.resolved.length === 0 ? (
        <p class="justice-muted">{t.noResolved}</p>
      ) : (
        <>
          <table class="table justice-table">
            <thead>
              <tr>
                <th scope="col">{t.columns.when}</th>
                <th scope="col">{t.columns.crime}</th>
                <th scope="col">{t.columns.offender}</th>
                <th scope="col">{t.columns.victims}</th>
                <th scope="col">{t.columns.response}</th>
                <th scope="col">{t.columns.reoffended}</th>
                <th scope="col">{t.columns.fate}</th>
              </tr>
            </thead>
            <tbody>
              {vm.resolved.map((c) => (
                <CaseRow key={c.id} c={c} />
              ))}
            </tbody>
          </table>
          {vm.resolvedTotal > vm.resolved.length && (
            <p class="justice-muted">{t.showing(vm.resolved.length, vm.resolvedTotal)}</p>
          )}
        </>
      )}
    </section>
  );
}
